import React from 'react';
import DialogContent, { DialogContentProps } from '@mui/material/DialogContent';
import Dialog, { DialogClasses } from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import DialogActions from '@mui/material/DialogActions';
import Button from '@merq/web-ui/dist/components/Button';
import GetAppSharpIcon from '@mui/icons-material/GetAppSharp';
import CloseIcon from 'images/icons/icon-close.svg';
import CloseDarkIcon from 'images/icons/icon-close-dark.svg';
import './styles.scss';
import { useTheme } from '@mui/material';

export interface Action {
  label: string;
  callback?: () => any;
  preventDefault?: boolean;
  disabled?: boolean;
  color?:
    | 'primary'
    | 'secondary'
    | 'error'
    | 'inherit'
    | 'success'
    | 'warning'
    | 'info'
    | undefined;
  variant?: 'text' | 'outlined' | 'contained' | undefined;
}
export interface ModalProps {
  downloadLink?: string;
  filename?: string;
  onDownload?: () => void;
  classes?: Partial<DialogClasses>;
  title?: string;
  sub?: string;
  actions?: Action[];
  openCallback?: () => void;
  closeCallback?: () => void;
  disableBackdropClick?: boolean;
  displayHeader?: boolean;
  dialogContentProps?: DialogContentProps;
}
export interface Props extends ModalProps {
  open: boolean;
  onClose: () => void;
}

export const Modal: React.FC<React.PropsWithChildren<Props>> = ({
  onClose,
  open,
  downloadLink,
  onDownload,
  title,
  classes = {} as DialogClasses,
  actions = [],
  disableBackdropClick,
  filename,
  children,
  displayHeader = true,
  dialogContentProps,
}) => {
  const theme = useTheme();

  const handleCallback = (action: Action) => () => {
    action.callback && action.callback();
    !action.preventDefault && onClose();
  };

  const handleClose = (_: any, reason: any) => {
    if (disableBackdropClick && reason && reason === 'backdropClick') return;
    onClose();
  };

  classes.root = `${classes.root || ''}, download-modal`;
  classes.paper = `${classes.paper || ''} modal-paper`;

  const closeIcon = theme.palette.mode === 'dark' ? CloseDarkIcon : CloseIcon;
  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="simple-dialog-title"
      open={open}
      maxWidth="lg"
      classes={classes}
    >
      {displayHeader && (
        <div className="modal-header">
          {!!title && (
            <div className="modal-title" data-testid="modal-title">
              {title}
            </div>
          )}
          {downloadLink && (
            <IconButton
              className="modal-button download"
              color="primary"
              component="a"
              href={downloadLink}
              onClick={onDownload}
              aria-label="download"
              download={filename || true}
              size="large"
            >
              <GetAppSharpIcon />
            </IconButton>
          )}
          <IconButton
            color="secondary"
            aria-label="close"
            className="modal-button"
            onClick={onClose}
            size="large"
          >
            <img src={closeIcon} alt="close dialog" />
          </IconButton>
        </div>
      )}
      <DialogContent className="modal-content" {...dialogContentProps}>
        {children}
      </DialogContent>
      {Boolean(actions.length) && (
        <DialogActions className="modal-actions">
          {actions.map((action, i) => (
            <Button
              onClick={handleCallback(action)}
              key={action.label}
              className="modal-cta"
              variant={action.variant || (i + 1 < actions.length ? 'outlined' : 'contained')}
              disabled={action.disabled}
              color={action.color || 'primary'}
            >
              {action.label}
            </Button>
          ))}
        </DialogActions>
      )}
    </Dialog>
  );
};

type closeCallback = () => void;
type closableChildren = (onClose: closeCallback) => React.ReactNode;

export default function useModal(
  document: React.ReactNode | closableChildren,
  modalProps: ModalProps = {}
) {
  const [open, setOpen] = React.useState(false);

  const handleClickOpen = () => {
    setOpen(true);
    modalProps.openCallback && modalProps.openCallback();
  };

  const handleClose = () => {
    setOpen(false);
    modalProps.closeCallback && modalProps.closeCallback();
  };

  return {
    onOpen: handleClickOpen,
    onClose: handleClose,
    component: (
      <Modal open={open} onClose={handleClose} {...modalProps}>
        {typeof document === 'function' ? document(handleClose) : document}
      </Modal>
    ),
  };
}
